// ThemisRuleSetEdit.tsx
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";

import { useAlert } from "../../../components/Alert/Alerts";
import Button from "../../../components/Button/Button";
import { CardBody } from "../../../components/DashboardCard/DashboardCard";
import TextField from "../../../components/FormFields/TextField";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";
import ToggleSwitchField from "../../../components/FormFields/ToggleSwitchField";
import Icon from "../../../components/Icon/Icon";

import ConfirmModal from "./ConfirmModal";
import {
  ThemisRuleSetDto,
  ThemisRuleSetRuleLinkDto,
  attachRuleToRuleSet,
  deleteRuleSetRule,
  getRuleSet,
  updateRuleSet,
  updateRuleSetRule,
} from "./manageThemisRuleSets";
import { ThemisPhaseOptions, ThemisRuleListItem } from "./ThemisRuleList";
import { getThemisRulesByPhase } from "./manageThemisRules";

// Layout
const PageWrap = styled.div`
  display: flex;
  gap: 2rem;
  width: 100%;
  align-items: stretch;
`;

const Panel = styled.div<{ $w?: "30" | "70" }>`
  flex: 0 0 ${(p) => (p.$w === "30" ? "30%" : p.$w === "70" ? "70%" : "50%")};
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.colorBorderLight};
  padding: 1.5rem;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SmallMuted = styled.p`
  font-size: 0.9rem;
  opacity: 0.7;
`;

const RuleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colorBorderLight};
  background: ${({ theme }) => theme.colorBackgroundLight};
  cursor: pointer;

  .name {
    margin-right: auto;
  }
`;

const PhaseRuleLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  width: 100%;
  align-items: flex-start;
`;

const PhaseColumn = styled.div`
  flex: 0 0 30%;
  min-width: 0;
`;

const RuleColumn = styled.div`
  flex: 1; /* fills remaining ~70% */
  min-width: 0;
`;

type RouteParams = {
  ruleSetId: string;
};

export default function ThemisRuleSetEdit() {
  const { addNewAlert } = useAlert();
  const history = useHistory();
  const { ruleSetId } = useParams<RouteParams>();

  const [ruleSet, setRuleSet] = useState<ThemisRuleSetDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // left panel
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  const [phaseRules, setPhaseRules] = useState<ThemisRuleListItem[]>([]);
  const [isLoadingPhase, setIsLoadingPhase] = useState(false);

  // confirm remove membership
  const [linkPendingDelete, setLinkPendingDelete] =
    useState<ThemisRuleSetRuleLinkDto | null>(null);

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {},
  });

  const id = Number(ruleSetId);

  const load = () => {
    if (!id) return;
    setIsLoading(true);
    getRuleSet(id)
      .then((res) => {
        if (res.status === 200) setRuleSet(res.data);
        else setRuleSet(null);
      })
      .catch(() => {
        setRuleSet(null);
        addNewAlert({ type: "error", message: "Failed to load RuleSet." });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  const sortedLinks = useMemo(() => {
    const links = ruleSet?.ruleLinks ?? [];
    return links
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [ruleSet]);

  const linkRuleIds = useMemo(() => {
    return new Set((ruleSet?.ruleLinks ?? []).map((l) => l.themisRuleId));
  }, [ruleSet]);

  const maxSortOrder = useMemo(() => {
    return sortedLinks.reduce((acc, l) => Math.max(acc, l.sortOrder ?? 0), 0);
  }, [sortedLinks]);

  useEffect(() => {
    if (!selectedPhase) {
      setPhaseRules([]);
      return;
    }

    setIsLoadingPhase(true);
    getThemisRulesByPhase(selectedPhase as any)
      .then((res) => {
        if (res.status === 200) setPhaseRules(res.data ?? []);
        else setPhaseRules([]);
      })
      .catch(() => {
        setPhaseRules([]);
        addNewAlert({ type: "error", message: "Failed to load phase rules." });
      })
      .finally(() => setIsLoadingPhase(false));
  }, [selectedPhase]);

  const saveHeader = async (
    patch: Partial<
      Pick<ThemisRuleSetDto, "name" | "description" | "isEnabled">
    >,
  ) => {
    if (!ruleSet) return;

    const next = {
      ...ruleSet,
      ...patch,
    };

    try {
      await updateRuleSet(ruleSet.id, {
        name: next.name,
        description: next.description ?? null,
        isEnabled: next.isEnabled,
        priority: next.priority, // read-only but required by backend DTO
      });

      setRuleSet(next);
      addNewAlert({ type: "success", message: "RuleSet updated." });
    } catch {
      addNewAlert({ type: "error", message: "Failed to update RuleSet." });
    }
  };

  const attachRule = async (rule: ThemisRuleListItem) => {
    if (!ruleSet) return;

    if (linkRuleIds.has(rule.id)) {
      addNewAlert({ type: "error", message: "Rule already attached." });
      return;
    }

    try {
      await attachRuleToRuleSet(ruleSet.id, {
        themisRuleId: rule.id,
        sortOrder: maxSortOrder + 10,
        isEnabled: true,
        overridePriority: null,
      });

      addNewAlert({ type: "success", message: "Rule added to RuleSet." });
      load();
    } catch {
      addNewAlert({ type: "error", message: "Failed to attach rule." });
    }
  };

  const toggleMembershipEnabled = async (
    link: ThemisRuleSetRuleLinkDto,
    enabled: boolean,
  ) => {
    try {
      await updateRuleSetRule(link.id, {
        sortOrder: link.sortOrder,
        isEnabled: enabled,
        overridePriority: link.overridePriority ?? null,
      });

      setRuleSet((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ruleLinks: prev.ruleLinks.map((l) =>
            l.id === link.id ? { ...l, isEnabled: enabled } : l,
          ),
        };
      });
    } catch {
      addNewAlert({ type: "error", message: "Failed to update membership." });
    }
  };

  const removeMembership = async () => {
    if (!linkPendingDelete) return;

    try {
      await deleteRuleSetRule(linkPendingDelete.id);
      addNewAlert({ type: "success", message: "Removed from RuleSet." });
      load();
    } catch {
      addNewAlert({ type: "error", message: "Failed to remove rule." });
    } finally {
      setLinkPendingDelete(null);
    }
  };

  if (isLoading && !ruleSet) return <CardBody>Loading...</CardBody>;
  if (!ruleSet) return <CardBody>RuleSet not found.</CardBody>;

  return (
    <FormikProvider value={formik}>
      <CardBody>
        <Row className="mb-[1rem]">
          <Button
            icon="arrow-left"
            onClick={() => history.push("/themis/rulesets")}
          >
            Back
          </Button>
          <h2 className="ml-[.5rem]">Edit RuleSet</h2>
        </Row>

        {/* Header fields */}
        <div className="mb-[1.5rem]">
          <SmallMuted>Key is not editable.</SmallMuted>

          <div className="grid grid-cols-2 gap-[1rem]">
            <TextField
              name="key"
              placeholder="Key"
              value={ruleSet.key}
              disabled
              className="w-full"
            />
            <TextField
              name="priority"
              placeholder="Priority"
              value={ruleSet.priority}
              disabled
              className="w-full"
            />

            <TextField
              name="name"
              placeholder="Name"
              value={ruleSet.name}
              className="w-full col-span-2"
              onChange={(e) =>
                setRuleSet((prev) =>
                  prev ? { ...prev, name: e.target.value } : prev,
                )
              }
              onBlur={() => saveHeader({ name: ruleSet.name })}
            />

            <TextField
              name="description"
              placeholder="Description"
              component="textarea"
              height="140px"
              value={ruleSet.description ?? ""}
              className="w-full col-span-2"
              onChange={(e) =>
                setRuleSet((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev,
                )
              }
              onBlur={() =>
                saveHeader({ description: ruleSet.description ?? "" })
              }
            />

            <div className="col-span-2">
              <ToggleSwitchField
                id="ruleset-enabled"
                name="isEnabled"
                label="Enabled"
                checked={ruleSet.isEnabled}
                onChange={(e) => saveHeader({ isEnabled: e.target.checked })}
              />
            </div>
          </div>
        </div>

        <PageWrap>
          {/* Left: available rules by phase */}
          <Panel>
            <h3 className="mb-[.25rem]">Add rules</h3>
            <SmallMuted className="mb-[1rem]">
              Select a phase, then click a rule to attach it to this RuleSet.
            </SmallMuted>
            <PhaseRuleLayout>
              {/* LEFT: phases */}
              <PhaseColumn>
                <p className="mb-[.5rem] font-semibold">Phases</p>

                <div className="flex flex-col gap-[.5rem]">
                  {ThemisPhaseOptions.map((p) => (
                    <Button
                      key={p.value}
                      className={`w-full justify-start ${
                        selectedPhase === p.value ? "" : "button-light"
                      }`}
                      onClick={() => setSelectedPhase(p.value)}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </PhaseColumn>

              {/* RIGHT: rules */}
              <RuleColumn>
                {selectedPhase ? (
                  <>
                    <SmallMuted className="mb-[.5rem]">
                      Phase{" "}
                      {ThemisPhaseOptions.find((x) => x.value === selectedPhase)
                        ?.label ?? selectedPhase}
                    </SmallMuted>

                    {isLoadingPhase && <p>Loading rules...</p>}

                    <div className="flex flex-col gap-[.5rem]">
                      {phaseRules.length > 0 ? (
                        phaseRules
                          .slice()
                          .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                          .map((r) => {
                            const already = linkRuleIds.has(r.id);

                            return (
                              <RuleRow
                                key={r.id}
                                onClick={() => {
                                  if (!already) void attachRule(r);
                                }}
                                style={{ opacity: already ? 0.5 : 1 }}
                              >
                                <span className="name">{r.name}</span>
                                <span className="text-xs opacity-70">
                                  {r.targetScopeKey}
                                </span>

                                {already && (
                                  <span className="text-xs opacity-70">
                                    Added
                                  </span>
                                )}

                                <span className="self-center">
                                  <Icon
                                    icon="plus"
                                    color="currentColor"
                                    width="16px"
                                    height="16px"
                                  />
                                </span>
                              </RuleRow>
                            );
                          })
                      ) : (
                        <SelectBox>No rules found for this phase</SelectBox>
                      )}
                    </div>
                  </>
                ) : (
                  <SelectBox>Please select a phase</SelectBox>
                )}
              </RuleColumn>
            </PhaseRuleLayout>
          </Panel>

          {/* Right: membership */}
          <Panel>
            <h3 className="mb-[.25rem]">Rules in this RuleSet</h3>
            <SmallMuted className="mb-[1rem]">
              Drag to reorder. Toggle Enabled to control membership. Remove
              deletes the link row.
            </SmallMuted>

            {sortedLinks.length > 0 ? (
              <div className="flex flex-col gap-[.5rem]">
                {sortedLinks.map((link) => (
                  <RuleRow key={link.id}>
                    <span className="name">{link.ruleName}</span>
                    <span className="text-xs opacity-70">{link.rulePhase}</span>
                    <span className="text-xs opacity-70">
                      {link.ruleTargetScopeKey}
                    </span>

                    <ToggleSwitchField
                      id={`link-enabled-${link.id}`}
                      name={`linkEnabled_${link.id}`}
                      label="Enabled"
                      checked={link.isEnabled}
                      small
                      onChange={(e) =>
                        void toggleMembershipEnabled(link, e.target.checked)
                      }
                    />

                    <span
                      className="self-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLinkPendingDelete(link);
                      }}
                    >
                      <Icon
                        icon="trash"
                        color="currentColor"
                        width="18px"
                        height="18px"
                      />
                    </span>
                  </RuleRow>
                ))}
              </div>
            ) : (
              <SelectBox>No rules attached yet</SelectBox>
            )}
          </Panel>
        </PageWrap>

        <ConfirmModal
          isOpen={!!linkPendingDelete}
          title="Remove rule?"
          message={
            linkPendingDelete
              ? `Remove "${linkPendingDelete.ruleName}" from this RuleSet? This will delete the membership row.`
              : ""
          }
          confirmText="Remove"
          cancelText="Cancel"
          onCancel={() => setLinkPendingDelete(null)}
          onConfirm={removeMembership}
        />
      </CardBody>
    </FormikProvider>
  );
}
